import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import "./maturity.less";
import { DebtMaturityEntry, Reit } from "../../../../types/reit.types";

interface MaturityProps {
  reit: Reit | null;
}

interface BarData {
  year: string;
  amount: number;
}

const Maturity: React.FC<MaturityProps> = ({ reit }) => {
  if (!reit) return null;

  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>(reit.years[0]?.year || "");
  const [quarter, setQuarter] = useState<string>("");
  const chartRef = useRef<SVGSVGElement | null>(null);

  const selectedYearData = useMemo(
    () => reit.years.find((y) => y.year === year),
    [reit, year]
  );

  const quarterOptions: string[] = useMemo(() => {
    const quarters = (selectedYearData as any)?.quarters || [];
    return quarters.map((q: any) => q.quarter);
  }, [selectedYearData]);

  const extractYear = (fyString: string): number => {
    const match = fyString.match(/FY-(\d+)/);
    if (match) {
      let yearNum = parseInt(match[1], 10);
      if (yearNum < 100) {
        yearNum += 2000;
      }
      return yearNum;
    }
    return 0;
  };

  const currentData: BarData[] = useMemo(() => {
    if (!selectedYearData) return [];

    const profile =
      view === "Yearly"
        ? selectedYearData.debtMaturityProfile?.debtMaturityProfile || []
        : (() => {
            const quarters = (selectedYearData as any)?.quarters || [];
            if (!quarters.length) return [];
            const qData =
              quarters.find((q: any) => q.quarter === quarter) || quarters[0];
            return qData.debtMaturityProfile?.debtMaturityProfile || [];
          })();

    const selectedYearNum = parseInt(year, 10);

    return profile
      .map(
        (p: DebtMaturityEntry): BarData => ({
          year: String(p.year?.value ?? ""),
          amount: Number(p.percentage?.value ?? 0),
        })
      )
      .filter((d: BarData) => {
        const dataYear = extractYear(d.year);
        return dataYear > selectedYearNum;
      })
      .filter((d: BarData) => d.amount > 0);
  }, [view, selectedYearData, quarter, year]);

  useEffect(() => {
    if (!chartRef.current) return;

    const svg = d3.select(chartRef.current);

    //Always clear previous chart
    svg.selectAll("*").remove();

    //If no data, stop here (after clearing)
    if (!currentData.length) return;

    const renderChart = () => {
      if (!chartRef.current) return;

      const margin = { top: 10, right: 40, bottom: 60, left: 80 };
      const width = chartRef.current.clientWidth - margin.left - margin.right;
      const height = 350;

      const svg = d3
        .select(chartRef.current)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

      // clear again before redraw
      svg.selectAll("*").remove();

      const bottomGap = 10;

      // --- Main group
      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // --- X scale
      const x = d3
        .scaleBand()
        .domain(currentData.map((d) => d.year))
        .range([0, width])
        .padding(0.4);

      // --- Y scale
      const y = d3
        .scaleLinear()
        .domain([0, d3.max(currentData, (d) => d.amount)! * 1.1])
        .nice()
        .range([height, 40]);

      // --- Grid lines
      g.append("g")
        .attr("class", "grid")
        .lower()
        .call(
          d3
            .axisLeft(y)
            .tickSize(-width)
            .tickFormat(() => "")
        )
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".tick line")
            .attr("stroke", "#D9E1EC")
            .attr("stroke-dasharray", "2,4")
        );

      g.select(".grid")
        .selectAll(".tick")
        .filter((d) => d === 0)
        .remove();

      // --- Bars
      g.append("g")
        .attr("class", "bars")
        .selectAll("rect")
        .data(currentData)
        .enter()
        .append("rect")
        .attr("x", (d) => x(d.year)!)
        .attr("y", height)
        .attr("width", x.bandwidth())
        .attr("height", 0)
        .attr("rx", 7)
        .attr("fill", "#14F2E73D")
        .transition()
        .duration(800)
        .attr("y", (d) => y(d.amount) - bottomGap)
        .attr("height", (d) => height - y(d.amount));

      // --- Axes
      g.append("g")
        .attr("transform", `translate(0, ${height})`)
        .call(d3.axisBottom(x).tickSize(0))
        .call((g) =>
          g
            .select(".domain")
            .attr("stroke", "#5A607F30")
            .attr("stroke-width", 1)
        )
        .selectAll("text")
        .style("font-size", "14px")
        .attr("dy", "1.5em")
        .style("font-family", "Duplet")
        .style("fill", "#5A607F");

      g.append("g")
        .call(d3.axisLeft(y).ticks(6).tickSize(0))
        .call((g) =>
          g
            .select(".domain")
            .attr("stroke", "#5A607F30")
            .attr("stroke-width", 1)
        )
        .call((g) =>
          g
            .selectAll(".tick text")
            .style("font-size", "14px")
            .attr("dx", "-0.7em")
            .style("font-family", "Duplet")
            .style("fill", "#5A607F")
        );

      // X-axis label
      g.append("text")
        .attr("class", "x-axis-label")
        .attr("x", width / 2)
        .attr("y", height + margin.bottom)
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .attr("fill", "#132c49")
        .text("Year");

      // Y-axis label
      g.append("text")
        .attr("class", "y-axis-label")
        .attr("x", -height / 2)
        .attr("y", -margin.left + 10)
        .attr("transform", "rotate(-90)")
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .attr("fill", "#132c49")
        .text("Amount (₹ Cr.)");

      // --- Hover effects
      g.selectAll(".bars rect")
        .on("mouseover", function () {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("fill", "#14F2E7")
            .style("filter", "drop-shadow(0px 9px 11.2px rgba(19,44,73,0.2))");
        })
        .on("mouseout", function () {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("fill", "#14F2E73D")
            .style("filter", "none");
        });
    };

    renderChart();
    window.addEventListener("resize", renderChart);

    return () => {
      window.removeEventListener("resize", renderChart);
    };
  }, [currentData]);

  return (
    <section className="maturity">
      <div className="maturity__card">
        <div className="maturity__header">
          <h2>Debt Maturity Profile</h2>
          <div className="maturity__subheader">
            <div className="maturity__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => {
                  setView("Quarterly");
                  if (quarterOptions.length) setQuarter(quarterOptions[0]);
                }}
              >
                Quarterly
              </button>
            </div>

            <div className="maturity__dropdowns">
              {view === "Quarterly" && quarterOptions.length > 0 && (
                <div className="maturity__dropdown" style={{ marginRight: 8 }}>
                  <div className="custom-select">
                    <select
                      value={quarter}
                      onChange={(e) => setQuarter(e.target.value)}
                    >
                      {quarterOptions.map((q, idx) => (
                        <option key={idx} value={q}>
                          {q}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="maturity__dropdown">
                <div className="custom-select">
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {reit.years.map((y, idx) => (
                      <option key={idx} value={y.year}>
                        FY {y.year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="maturity__body">
          <div className="maturity__chart-box">
            <div className="maturity__chart-area">
              <svg ref={chartRef} />
            </div>
          </div>

          <div className="maturity__note">
            <p>
              Note: The final year of the REIT's data includes debt figures
              subsequent to the end of quarter/year.
            </p>
          </div>
        </div>

        <div className="maturity__table">
          <table>
            <thead>
              <tr>
                <th>Year</th>
                {currentData.map((d, idx) => (
                  <th key={idx}>{d.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Amount (₹ Cr.)</td>
                {currentData.map((d, idx) => (
                  <td key={idx}>
                    {d.amount ? d.amount.toLocaleString() : "-"}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Maturity;
